import * as React from "react";
import { AppDispatch, IAppStore } from "app/store";
import { connect } from "react-redux";
import { withRouter, match, RouteComponentProps } from "react-router-dom";
import classNames from "classnames";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";
import debounce from "lodash/debounce";
import * as QueryString from "query-string";
import axios from "axios";
// interfaces
import { IAppState } from "app/rootReducer";
// actions
import {
  searchInterests,
  searchCards,
  ActionCreators,
  searchUsers,
  searchQuestions,
} from "@vingle/wtf-sdk/actions/search";
import { ActionCreators as InterestActionCreators } from "app/interest/actions";
import { ActionCreators as GlobalDialogActionCreators } from "app/vingleDialog/actions";
import {
  paginatedCardDenormalizer,
  usersDenormalizer,
  paginatedQuestionsDenormalizer,
} from "@vingle/wtf-sdk/models/denormalizer";
import { changeUserFollowing } from "@vingle/wtf-sdk/actions/common";
import { unlikeQuestion, likeQuestion } from "@vingle/wtf-sdk/actions/question";
import { isModalSelector } from "@vingle/wtf-sdk/selectors/app";
// component
import { IconWrapper } from "common/icons";
import PageUpdater from "@vingle/wtf-sdk/components/pageUpdater";
import InterestList from "./components/interestList";
import UserList, { Type as UserListType } from "common/components/userList";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import RoutedVingleTab from "common/components/vingleTab/routed";
import EmptyMessage from "common/components/emptyMessage";
import QuestionList from "common/components/questionList";
import ScrollToTopOnMount from "@vingle/wtf-sdk/components/scrollToTopOnMount";
import CompactList from "common/components/compactList";
// resource
import PACKS from "common/icons/packs";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { interestFollow } from "@vingle/wtf-sdk/actions/interest";
// styles

const styles = require("./search.scss");

interface ISearchContainerProps extends ReturnType<typeof mapStateToProps> {
  dispatch: AppDispatch;
}

function mapStateToProps(state: IAppState) {
  const entities = state.entities;
  return {
    currentUser: state.app.currentUser,
    interests: state.searchData.interests,
    cards: paginatedCardDenormalizer(state.searchData.cards, entities),
    questions: paginatedQuestionsDenormalizer(
      state.searchData.questions,
      entities,
    ),
    users: usersDenormalizer(state.searchData.users, entities),
    searchPage: state.searchPage,
    isNotCreatedInterest: state.searchData.isNotCreatedInterest,
    isModal: isModalSelector(state),
  };
}

function parseQuery(search: string) {
  try {
    if (search) {
      return decodeURIComponent(QueryString.parse(search).q as string);
    }
    return "";
  } catch (_err) {
    return "";
  }
}

class PureSearchContainer extends React.PureComponent<
  ISearchContainerProps &
    RouteComponentProps<Vingle.IMatchParams> &
    WrappedComponentProps
> {
  private cancelTokenSource = axios.CancelToken.source();

  public componentDidMount() {
    this.searchItemsInitially();
  }

  public componentDidUpdate(
    prevProps: ISearchContainerProps & RouteComponentProps<Vingle.IMatchParams>,
  ) {
    if (
      !this.props.isModal &&
      parseQuery(this.props.location.search) !==
        parseQuery(prevProps.location.search)
    ) {
      this.searchItemsInitially();
      this.cancelTokenSource = axios.CancelToken.source();
    }
  }

  public componentWillUnmount() {
    this.cancelTokenSource.cancel();
  }

  public render() {
    const query = this.getQuery();
    const searchParams = { q: query };
    return (
      <div>
        <ScrollToTopOnMount />
        <PageUpdater title={`Search results for ${query}`} />
        <div className={styles.contentWrapper}>
          {this.renderHeader()}
          <RoutedVingleTab
            vingleTabType="main"
            tabs={[
              {
                key: `interests`,
                url: new VingleURL.SearchInterest(),
                qs: searchParams,
                title: <FormattedMessage id="search_page.tabs.interests" />,
                page: this.renderInterests,
              },
              {
                key: `users`,
                url: new VingleURL.SearchUsers(),
                qs: searchParams,
                title: <FormattedMessage id="search_page.tabs.users" />,
                page: this.renderUsers,
              },
              {
                key: `cards`,
                url: new VingleURL.SearchCards(),
                qs: searchParams,
                title: <FormattedMessage id="search_page.tabs.cards" />,
                page: this.renderCards,
                default: true,
              },
              {
                key: `questions`,
                url: new VingleURL.SearchQuestions(),
                qs: searchParams,
                title: <FormattedMessage id="search_page.tabs.questions" />,
                page: this.renderQuestions,
              },
            ]}
          />
        </div>
      </div>
    );
  }

  private readonly searchItemsInitially = () => {
    const { dispatch } = this.props;
    const query = this.getQuery();
    if (query) {
      dispatch(ActionCreators.clearSearchedCards());
      dispatch(ActionCreators.clearSearchedQuestions());
      dispatch(searchInterests({ query }));
      dispatch(searchCards({ query }));
      dispatch(searchQuestions({ query }));
      dispatch(searchUsers({ query }));
    }
  };

  private readonly getQuery = () => {
    const { location } = this.props;
    return parseQuery(location.search);
  };

  private readonly renderHeader = () => {
    return (
      <div className={styles.header}>
        <div className={styles.title}>
          <IconWrapper
            icon={PACKS.SEARCH_DISCOVER.SEARCH}
            className={styles.icon}
          />
          <div className={styles.text} title={this.getQuery()}>
            {this.getQuery()}
          </div>
        </div>
      </div>
    );
  };

  private readonly renderLoading = (isInfinityLoading: boolean = false) => {
    return (
      <div
        className={classNames(styles.loading, {
          [styles.infinityLoading]: isInfinityLoading,
        })}
      >
        <PulseLoadingDot />
      </div>
    );
  };

  private readonly renderInterests = () => {
    const { interests, searchPage, isNotCreatedInterest } = this.props;
    let loading;

    if (searchPage.isInterestLoading) {
      loading = this.renderLoading();
    }

    return (
      <div className={styles.results}>
        <InterestList
          isNotCreatedInterest={isNotCreatedInterest}
          interests={interests}
          onFollowClick={this.handleInterestFollowClick}
        />
        {loading}
      </div>
    );
  };

  private readonly renderCards = () => {
    const { cards, searchPage } = this.props;
    const referralUrl = `${new VingleURL.SearchCards().toSchema()}?q=${this.getQuery()}`;
    return (
      <div className={styles.results}>
        <CompactList
          cards={cards.data}
          handleInfinityLoad={this.debouncedHandleInfinityLoad}
          loader={this.renderLoading(true)}
          isLoading={searchPage.isCardLoading}
          emptyElement={
            <EmptyMessage
              messageId="search_page.card.no_cards"
              style={{ fontWeight: 300, marginTop: "119px" }}
            />
          }
          paging={cards.paging}
          trackTicket={{
            contentType: "post",
            referralUrl,
          }}
        />
      </div>
    );
  };

  private readonly renderUsers = () => {
    const { users, searchPage, currentUser } = this.props;

    if (searchPage.isUserLoading) {
      return this.renderLoading();
    }

    return (
      <div className={classNames(styles.results, styles.userListWrapper)}>
        <UserList
          currentUser={currentUser}
          users={users}
          onFollowClick={this.handleUserFollowClick}
          type={UserListType.Card}
          emptyElement={
            <EmptyMessage
              messageId="search_page.card.no_cards"
              style={{ fontWeight: 300, marginTop: "150px" }}
            />
          }
          listClassName={styles.userList}
          itemClassName={styles.userItem}
        />
      </div>
    );
  };

  private readonly renderQuestions = () => {
    const { questions, searchPage } = this.props;
    let loading;

    if (searchPage.isQuestionLoading) {
      loading = this.renderLoading();
    } else if (questions.data.length === 0) {
      return (
        <EmptyMessage
          messageId="search_page.card.no_cards"
          style={{ fontWeight: 300, marginTop: "150px" }}
        />
      );
    }

    return (
      <div className={styles.results}>
        <QuestionList
          questions={questions.data}
          onLikeClick={this.handleLikeClick}
        />
        {loading}
      </div>
    );
  };

  private readonly handleInfinityLoad = () => {
    const { dispatch, cards, searchPage } = this.props;
    const query = this.getQuery();
    const after = cards.paging.after;

    if (!searchPage.isCardLoading && after && query) {
      return dispatch(searchCards({ query, after }));
    }
  };

  // tslint:disable-next-line:member-ordering
  private readonly debouncedHandleInfinityLoad = debounce(
    this.handleInfinityLoad,
  );

  private readonly handleInterestFollowClick = (
    interest: Vingle.Interest.IInterest,
  ) => {
    const { dispatch, currentUser, isNotCreatedInterest } = this.props;
    if (currentUser && interest) {
      if (
        (interest.relation && interest.relation.following) ||
        isNotCreatedInterest
      ) {
        this.openInterestFollowDialog(interest);
      } else {
        dispatch(
          interestFollow({
            user: currentUser,
            interest: interest.name,
            community: interest.community,
          }),
        );
      }
    } else {
      dispatch(GlobalDialogActionCreators.openSignUpDialog());
    }
  };

  private readonly handleUserFollowClick = (user: Vingle.User.IListUser) => {
    const { dispatch, currentUser } = this.props;
    if (currentUser && user) {
      if (user.relation && user.relation.following) {
        dispatch(
          changeUserFollowing({
            userId: user.id,
            username: user.username,
            followingState: false,
            cancelToken: this.cancelTokenSource.token,
          }),
        );
      } else {
        dispatch(
          changeUserFollowing({
            userId: user.id,
            username: user.username,
            followingState: true,
            cancelToken: this.cancelTokenSource.token,
          }),
        );
      }
    } else {
      dispatch(GlobalDialogActionCreators.openSignUpDialog());
    }
  };

  private readonly handleLikeClick = (
    questionId: string,
    currentLikeState: boolean,
  ) => {
    const { dispatch } = this.props;
    if (currentLikeState) {
      dispatch(unlikeQuestion(questionId, this.cancelTokenSource.token));
    } else {
      dispatch(likeQuestion(questionId, this.cancelTokenSource.token));
    }
  };

  private readonly openInterestFollowDialog = (
    interest: Vingle.Interest.IInterest,
  ) => {
    const { dispatch, intl, isNotCreatedInterest } = this.props;
    const isFollowing = Boolean(
      interest && interest.relation && interest.relation.following,
    );
    const succeedMessage =
      (isNotCreatedInterest &&
        intl.formatMessage(
          { id: "search_page.interest.interest_create_dialog.toast_message" },
          { interest: interest.name },
        )) ||
      undefined;

    dispatch(
      InterestActionCreators.openInterestFollowConfirmDialog({
        type: isNotCreatedInterest ? "create" : "remove",
        interest: interest.name,
        isFollowing,
        community: interest.community,
        succeedMessage,
      }),
    );
  };
}

export function getInitialData({
  appStore,
  requestUrl,
}: {
  match: match<Vingle.IMatchParams>;
  appStore: IAppStore;
  requestUrl: string;
}) {
  const {
    query: { q },
    url,
  } = QueryString.parseUrl(requestUrl);
  if (!VingleURL.Search.isSameExact(url) && q) {
    return Promise.all([
      appStore.dispatch(searchInterests({ query: q })),
      appStore.dispatch(searchCards({ query: q })),
      appStore.dispatch(searchUsers({ query: q })),
      appStore.dispatch(searchQuestions({ query: q })),
    ]);
  }
  return Promise.resolve();
}

const SearchContainer = PureSearchContainer;
export { SearchContainer as PureSearchContainer };
export default withRouter(
  connect(mapStateToProps)(injectIntl(SearchContainer)),
);
